import { useEffect, useState } from 'react';
import { get } from '../../lib/api-client';

interface DailyCost {
  date: string;
  cost: number;
  inputTokens: number;
  outputTokens: number;
}

interface UserCost {
  userId: string;
  cost: number;
  inputTokens: number;
  outputTokens: number;
  requestCount: number;
}

interface CostsResponse {
  month: string;
  totalCost: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  topUsers: UserCost[];
  dailyBreakdown: DailyCost[];
}

export function Costs() {
  const [data, setData] = useState<CostsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<CostsResponse>('/v1/admin/costs')
      .then(setData)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load costs'),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-ll-muted">Loading...</p>;
  if (error)
    return (
      <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
        {error}
      </div>
    );
  if (!data) return null;

  const maxDailyCost = Math.max(...data.dailyBreakdown.map((d) => d.cost), 0.01);

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold text-ll-text">AI Cost Monitoring</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-ll-surface border border-ll-border rounded-xl p-5">
          <p className="text-xs uppercase font-mono text-ll-muted mb-1">Monthly Cost</p>
          <p className="text-3xl font-bold text-ll-cyan">${data.totalCost.toFixed(2)}</p>
          <p className="text-xs text-ll-muted mt-1">{data.month}</p>
        </div>
        <div className="bg-ll-surface border border-ll-border rounded-xl p-5">
          <p className="text-xs uppercase font-mono text-ll-muted mb-1">Input Tokens</p>
          <p className="text-2xl font-bold text-ll-text">
            {data.totalInputTokens.toLocaleString()}
          </p>
        </div>
        <div className="bg-ll-surface border border-ll-border rounded-xl p-5">
          <p className="text-xs uppercase font-mono text-ll-muted mb-1">Output Tokens</p>
          <p className="text-2xl font-bold text-ll-text">
            {data.totalOutputTokens.toLocaleString()}
          </p>
        </div>
      </div>

      {data.dailyBreakdown.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ll-text mb-3">Daily Cost</h3>
          <div className="flex items-end gap-[2px] h-32">
            {data.dailyBreakdown.map((d) => (
              <div
                key={d.date}
                className="flex-1 bg-ll-cyan/60 rounded-t hover:bg-ll-cyan transition-colors"
                style={{
                  height: `${(d.cost / maxDailyCost) * 100}%`,
                  minHeight: d.cost > 0 ? '4px' : '0',
                }}
                title={`${d.date}: $${d.cost.toFixed(4)}`}
              />
            ))}
          </div>
        </div>
      )}

      {data.topUsers.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ll-text mb-3">Top Users by Cost</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ll-border text-left">
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">User ID</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Cost</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Requests</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">
                    Input Tokens
                  </th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">
                    Output Tokens
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.topUsers.map((user) => (
                  <tr key={user.userId} className="border-b border-ll-border/50">
                    <td className="py-2 px-3 text-ll-text font-mono text-xs">{user.userId}</td>
                    <td className="py-2 px-3 text-ll-cyan">${user.cost.toFixed(4)}</td>
                    <td className="py-2 px-3 text-ll-text">{user.requestCount}</td>
                    <td className="py-2 px-3 text-ll-muted">{user.inputTokens.toLocaleString()}</td>
                    <td className="py-2 px-3 text-ll-muted">
                      {user.outputTokens.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
